/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiUserBaseQuery } from './apiSlice';

/** Conferencer table row as returned by the API. */
export type ConferencerRecord = {
  id: string;
  caller_mta: string;
  callee_mta: string;
  meta: Record<string, unknown> | null;
  acknowledgement_status: string;
  call_status: string | null;
  recording_meta: Record<string, unknown> | null;
  extras: Record<string, unknown> | null;
  created_at: string;
  last_updated_at: string;
  caller_number: string;
  callee_number: string;
  callee_response_to_call?: string | null;
  provider_action_on_call?: string | null;
  participants_in_room?: number | null;
  caller_start_call_at?: string | null;
  callee_response_to_call_at?: string | null;
  provider_action_on_call_at?: string | null;
  caller_rejected_at?: string | null;
  caller_hangup_at?: string | null;
  callee_hangup_at?: string | null;
  caller_speech_seconds?: number;
  callee_speech_seconds?: number;
};

export type ConferencerSummary = {
  totalRecordingHours: number;
  uniqueWorkers: number;
  totalCalls: number;
  avgCallsPerDay: number;
  usersWithCallAboveMinutes: number;
  successfulCalls: number;
  callsAboveDuration: number;
  callsDroppedDueToNetwork: number;
  callsRejectedByCaller: number;
  callsRejectedByCallee: number;
};

export type ConferencerSummaryRow = ConferencerSummary & { language: string };

type GetConferencerSummaryArg = {
  afterDate: string;
  durationMinutes?: number;
};

export type ConferencerRecordsFilter = {
  id?: string;
  callerNumber?: string;
  calleeNumber?: string;
  roomName?: string;
  afterDate?: string;
  limit?: number;
  offset?: number;
};

export type ConferencerRecordsSummary = {
  totalCalls: number;
  successfulCalls: number;
  totalRecordingMinutes: number;
  totalCallerSpeechSeconds: number;
  totalCalleeSpeechSeconds: number;
};

const DEFAULT_RECORDS_LIMIT = 10;

function buildRecordsQueryString(filters: ConferencerRecordsFilter): string {
  const params = new URLSearchParams();
  if (filters.id?.trim()) params.set('id', filters.id.trim());
  if (filters.callerNumber?.trim()) params.set('callerNumber', filters.callerNumber.trim());
  if (filters.calleeNumber?.trim()) params.set('calleeNumber', filters.calleeNumber.trim());
  if (filters.roomName?.trim()) params.set('roomName', filters.roomName.trim());
  if (filters.afterDate?.trim()) params.set('afterDate', filters.afterDate.trim());
  params.set('limit', String(filters.limit ?? DEFAULT_RECORDS_LIMIT));
  params.set('offset', String(filters.offset ?? 0));
  return params.toString();
}

export const conferencerApi = createApi({
  reducerPath: 'conferencerApi',
  baseQuery: apiUserBaseQuery,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getConferencerSummary: builder.query<ConferencerSummaryRow[], GetConferencerSummaryArg>({
      query: ({ afterDate, durationMinutes = 5 }) => {
        const params = new URLSearchParams({ afterDate });
        params.set('durationMinutes', String(durationMinutes));
        return { url: `/conferencer/summary?${params.toString()}`, method: 'GET' };
      },
    }),
    getConferencerRecords: builder.query<ConferencerRecord[], ConferencerRecordsFilter>({
      query: (filters) => {
        const qs = buildRecordsQueryString(filters);
        return {
          url: `/conferencer/records${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
    }),
    getConferencerRecordsSummary: builder.query<ConferencerRecordsSummary, ConferencerRecordsFilter>({
      query: (filters) => {
        const qs = buildRecordsQueryString(filters);
        return {
          url: `/conferencer/records/summary${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
    }),
  }),
});

export const {
  useGetConferencerSummaryQuery,
  useLazyGetConferencerSummaryQuery,
  useLazyGetConferencerRecordsQuery,
  useLazyGetConferencerRecordsSummaryQuery,
} = conferencerApi;
